/**
 * DoppelgangerSkillEffect — Specter phantom-path evolution: replaces Phantom.
 *
 * A more convincing decoy echo. Same instant-activation, same fizzle gate,
 * same summon pipeline — but when the phantom expires and enemies arrive at
 * its position to find nothing, they enter a confused state for 4 seconds:
 * detection radius drops 25%, movement slows 20%, backup calls suppressed.
 * The lie failing IS the weapon.
 *
 * Inherits all Phantom upgrade hooks through `replacesId: 'phantom'`:
 * - Mirror (phantomMoves / phantomPatrolRadius): patrol movement
 * - Linger (phantomDurationMs / investigationWindowMs): extended duration
 * - Redirect (postInvestigationSpeedReduction / patrolResumeDelayMs): post-investigation
 *   slow applied after confused expires (sequential layers, not simultaneous)
 *
 * Resolver queries use scope 'phantom' so existing upgrade wiring flows
 * through the replacesId chain without modification.
 *
 * Confused-state parameters are data-driven from status-effects.json:
 * detectionMultiplier (0.75), speedMultiplier (0.8), suppressBackup (true),
 * defaultDurationMs (4000).
 *
 * Implements SkillEffect directly — same architecture as PhantomSkillEffect.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Doppelganger
 * @see status-effects.json § confused
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DoppelgangerDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
    hasEffect(skillId: string, key: string): boolean;
    getTreePassiveEffect(treeName: string, key: string, aggregation: 'sum' | 'max'): number;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
    getTargetPosition(): { x: number; y: number };
  };
  readonly summons: {
    spawn(config: DoppelgangerSummonConfig, x: number, y: number): string;
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs: number;
      targetEntityId: string;
    }): void;
  };
}

/** Config object passed to SummonSystem.spawn() for doppelganger decoys. */
export interface DoppelgangerSummonConfig {
  readonly id: string;
  readonly archetype: 'decoy';
  readonly baseHp: number;
  readonly baseDamage: number;
  readonly baseSpeed: number;
  readonly leashRadiusTiles: number;
  readonly durationMs: number;
  readonly aggroWeight: number;
  readonly onExpiry: (investigatingEnemyIds: string[]) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DoppelgangerConfig {
  readonly cooldownMs: number;
  readonly baseDurationMs: number;
  readonly decoyAggroWeight: number;
  readonly decoyHp: number;
  readonly confusedDurationMs: number;
}

const DEFAULT_CONFIG: DoppelgangerConfig = {
  cooldownMs: 1200,               // medium cooldown tier (same as Phantom)
  baseDurationMs: 6000,           // same base duration as Phantom
  decoyAggroWeight: 1.0,          // full aggro redirect
  decoyHp: 9999,                  // effectively invulnerable
  confusedDurationMs: 4000,       // status-effects.json: confused defaultDurationMs
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DoppelgangerSkillEffect implements SkillEffect {
  readonly skillId = 'doppelganger';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: DoppelgangerDeps;
  private readonly config: DoppelgangerConfig;

  private _cooldownRemaining = 0;

  // Activation-result state (observable by scenarios and game layer)
  private _spawnsCount = 0;
  private _fizzled = false;
  private _lastSpawnedId = '';
  private _resolvedDurationMs = 0;
  private _resolvedPatrolRadius = 0;
  private _resolvedInvestigationWindowMs = 0;
  private _hasMirror = false;
  private _confusedAppliedTo: string[] = [];
  private _redirectAppliedTo: string[] = [];
  private _resolvedRedirectSpeedReduction = 0;
  private _resolvedPatrolResumeDelayMs = 0;

  // Pending Redirect: applied after confused expires (sequential, not simultaneous)
  private _pendingRedirectEnemyIds: string[] = [];
  private _pendingRedirectTimerMs = 0;

  constructor(deps: DoppelgangerDeps, config: Partial<DoppelgangerConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get spawnsCount(): number { return this._spawnsCount; }
  get fizzled(): boolean { return this._fizzled; }
  get lastSpawnedId(): string { return this._lastSpawnedId; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }
  get resolvedPatrolRadius(): number { return this._resolvedPatrolRadius; }
  get resolvedInvestigationWindowMs(): number { return this._resolvedInvestigationWindowMs; }
  get hasMirror(): boolean { return this._hasMirror; }
  get confusedAppliedTo(): readonly string[] { return this._confusedAppliedTo; }
  get redirectAppliedTo(): readonly string[] { return this._redirectAppliedTo; }
  get resolvedRedirectSpeedReduction(): number { return this._resolvedRedirectSpeedReduction; }
  get resolvedPatrolResumeDelayMs(): number { return this._resolvedPatrolResumeDelayMs; }
  get pendingRedirectTimerMs(): number { return this._pendingRedirectTimerMs; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Resolve duration: base + Linger additive bonus (scope: 'phantom' via replacesId)
    const lingerBonus = this.deps.resolver.getAdditiveEffect(
      'phantom', 'phantomDurationMs',
    );
    this._resolvedDurationMs = this.config.baseDurationMs + lingerBonus;

    // Resolve investigation window bonus
    this._resolvedInvestigationWindowMs = this.deps.resolver.getAdditiveEffect(
      'phantom', 'investigationWindowMs',
    );

    // Resolve Mirror: patrol movement
    this._hasMirror = this.deps.resolver.hasEffect('phantom', 'phantomMoves');
    const maxRadius = this.deps.resolver.getMaxEffect('phantom', 'phantomPatrolRadius');
    this._resolvedPatrolRadius = this._hasMirror && maxRadius !== undefined ? maxRadius : 0;

    // Resolve Redirect (phantom_path_passive): post-investigation slow + patrol delay
    this._resolvedRedirectSpeedReduction = this.deps.resolver.getTreePassiveEffect(
      'specter', 'postInvestigationSpeedReduction', 'sum',
    );
    this._resolvedPatrolResumeDelayMs = this.deps.resolver.getTreePassiveEffect(
      'specter', 'patrolResumeDelayMs', 'sum',
    );
    this._redirectAppliedTo = [];
    this._pendingRedirectEnemyIds = [];
    this._pendingRedirectTimerMs = 0;

    // Build summon config with onExpiry callback for confused-state application
    const confusedDurationMs = this.config.confusedDurationMs;
    const redirectReduction = this._resolvedRedirectSpeedReduction;
    const summonConfig: DoppelgangerSummonConfig = {
      id: 'doppelganger_decoy',
      archetype: 'decoy',
      baseHp: this.config.decoyHp,
      baseDamage: 0,
      baseSpeed: this._hasMirror ? 1.0 : 0,
      leashRadiusTiles: this._resolvedPatrolRadius,
      durationMs: this._resolvedDurationMs,
      aggroWeight: this.config.decoyAggroWeight,
      onExpiry: (investigatingEnemyIds: string[]) => {
        this._confusedAppliedTo = [...investigatingEnemyIds];
        for (const enemyId of investigatingEnemyIds) {
          this.deps.statusEffects.apply('confused', {
            sourceSkillId: 'doppelganger',
            durationMs: confusedDurationMs,
            targetEntityId: enemyId,
          });
        }
        // Schedule Redirect slow after confused expires (sequential layers)
        if (redirectReduction > 0 && investigatingEnemyIds.length > 0) {
          this._pendingRedirectEnemyIds = [...investigatingEnemyIds];
          this._pendingRedirectTimerMs = confusedDurationMs;
        }
      },
    };

    // Spawn the doppelganger at the targeted location
    const target = this.deps.targeting.getTargetPosition();
    this._lastSpawnedId = this.deps.summons.spawn(summonConfig, target.x, target.y);
    this._spawnsCount++;

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick pending Redirect: apply post_investigation_slow after confused expires
    if (this._pendingRedirectTimerMs > 0) {
      this._pendingRedirectTimerMs = Math.max(0, this._pendingRedirectTimerMs - delta);
      if (this._pendingRedirectTimerMs <= 0) {
        this._redirectAppliedTo = [...this._pendingRedirectEnemyIds];
        const patrolDelay = this._resolvedPatrolResumeDelayMs;
        for (const enemyId of this._pendingRedirectEnemyIds) {
          this.deps.statusEffects.apply('post_investigation_slow', {
            sourceSkillId: 'doppelganger',
            durationMs: patrolDelay > 0 ? patrolDelay : 3000,
            targetEntityId: enemyId,
          });
        }
        this._pendingRedirectEnemyIds = [];
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
